import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, array, object, string } from 'yup';

export type CreateProviderFormState = {
  name: string;
  apiUrl: string;
  syncRoundEnd: string | boolean;
  status: string;
  retryScheduledSendCredit: string | boolean;
  retryConfigId: string | null;
  refundOnCreditFail: string | boolean;
  filterExternalProviderBets: string | boolean;
  isInternal: string | boolean;
  configId: string | undefined;
  creditOnDisconnect: string | boolean;
  games: number[];
  integrationType: string;
};

export const createProviderFormStateDefault: CreateProviderFormState = {
  name: '',
  apiUrl: '',
  syncRoundEnd: '',
  status: '',
  retryScheduledSendCredit: '',
  refundOnCreditFail: '',
  retryConfigId: null,
  filterExternalProviderBets: '',
  isInternal: '',
  configId: undefined,
  creditOnDisconnect: '',
  games: [],
  integrationType: '',
};

export const createProviderSchemaDefault: ObjectSchema<Partial<CreateProviderFormState>> = object<
  Partial<CreateProviderFormState>
>().shape({
  name: string().required('name is required field'),
  apiUrl: string().required('api url is required field'),
  syncRoundEnd: string().required('sync round end is required field'),
  status: string().required('status round end is required field'),
  retryScheduledSendCredit: string().required('retry scheduled send credit is required field'),
  retryConfigId: string().nullable('retry config id is required field'),
  refundOnCreditFail: string().required('refund on credit fail is required field'),
  filterExternalProviderBets: string().required('filterexternal provider bets is required field'),
  isInternal: string().required('is internal is required field'),
  configId: string().optional(),
  creditOnDisconnect: string().required('credit on disconnect is required field'),
  games: array()
    .min(1, 'games array must have at least one game')
    .required('games is required field'),
  integrationType: string().required('integration type is required field'),
});

type CreateProviderFormContextType = {
  schema: ObjectSchema<Partial<CreateProviderFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateProviderFormState>>>;
  methods?: UseFormReturn<CreateProviderFormState>;
};

const createProviderFormContextDefault = {
  schema: createProviderSchemaDefault,
  setSchema: noop,
} satisfies CreateProviderFormContextType;

export const CreateProviderFormContext = createContext<CreateProviderFormContextType>(
  createProviderFormContextDefault
);

export const useCreateProviderForm = () => {
  const { methods } = useContext(CreateProviderFormContext);
  if (!methods) {
    throw new Error('CreateProviderFormContext is empty');
  }

  return methods;
};

export const useManageCreateProviderSchema = () => {
  const { setSchema } = useContext(CreateProviderFormContext);

  return { setSchema };
};
